import ReactECharts from "echarts-for-react";
import { useEffect, useRef, useState } from "react";
import styles from "styles/pages/reports/reports.module.scss";
import ComboFilters from "./ComboFilters";
import classNames from "classnames/bind";
import {
  getAllMonthsUntilNow,
  getCurrentMonthYear,
  getGrowthPercentage,
  getLastSixYears,
  months,
} from "./Reports";
import { If } from "shared/components/control-statements";
import { Numbers } from "shared/utils/Numbers";
import dayjs from "dayjs";
import { Loading } from "@components";
const css = classNames.bind(styles);

const Trend = ({ growth, selectedFilter, invertColorSign }) => {
  const upColor = invertColorSign ? "#FF4C4C" : "#51D9A3";
  const downColor = invertColorSign ? "#51D9A3" : "#FF4C4C";

  const isZero = growth === 0 || growth === null;
  const isNegative = growth < 0;
  const sign = isNegative ? "" : growth > 0 ? "+" : "";
  const iconClass = isNegative
    ? "fas fa-arrow-trend-down"
    : "far fa-arrow-trend-up";
  const iconColor = isZero ? "#999" : isNegative ? downColor : upColor;

  return (
    <div className={`${styles.growth} ${isNegative ? styles.decrease : ""}`}>
      <div className="d-flex align-center gap-5">
        <p className={styles.growthValue} style={{ color: iconColor }}>
          <If cond={isZero}>---</If>
          <If cond={!isZero}>{`${sign}${Numbers.percent(growth) || 0}`}</If>
        </p>
        <If cond={!isZero}>
          <i className={iconClass} style={{ color: iconColor }} />
        </If>
      </div>
      <p className={styles.growthText}>
        Crescimento no {selectedFilter === "year" ? "ano" : "mês"}
      </p>
    </div>
  );
};

export const MonthYearChart = ({
  selectedData,
  enableMonth = true,
  onFetchData,
  onClose,
  loading,
  startOn2025,
  invertColorSign,
  skipCurrentMonth,
  startOnMonth,
  startOnYear,
}) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = getCurrentMonthYear();

  const [data, setData] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("year");
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState({
    name: currentYear,
    id: currentYear,
  });

  const formattedData = (data || []).map((item) => {
    if (typeof item === "object") {
      return {
        ...item,
        // value: +item?.value,
        value: +item?.value === 0 ? null : +item?.value,
      };
    }
    return item === 0 ? null : item;
  });

  const daysInMonth = Array.from(
    {
      length: dayjs(
        `${selectedMonth.year}-${String(selectedMonth.id).padStart(2, "0")}`
      )
        .endOf("month")
        .date(),
    },
    (_, i) => i + 1
  );

  // console.log("data", data);

  const dataValues = (data || []).map((item) => item?.value);

  const growth = getGrowthPercentage({
    data: dataValues,
    selectedFilter,
    selectedMonth,
    selectedYear,
  });

  // console.log("dataValues", dataValues, growth);

  const option = {
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "none",
      },
      backgroundColor: "#2B2B2B",
      borderRadius: 8,
      padding: [8, 8],
      borderWidth: 0,
      textStyle: {
        color: "#FFF",
        fontSize: 10,
      },
      formatter: (params) => {
        const p = params[0];

        const invalid =
          p?.data?.value === null ||
          p?.data?.value === undefined ||
          p?.data?.value === "" ||
          Number.isNaN(p?.data?.value);

        if (invalid) return "";

        const trendHtmlString = (growth) => {
          const upColor = invertColorSign ? "#FF4C4C" : "#51D9A3";
          const downColor = invertColorSign ? "#51D9A3" : "#FF4C4C";

          if (growth > 0) {
            return `<span style="color:${upColor};font-weight:800">+${Numbers.percent(
              growth,
              2
            )} ↑</span>`;
          } else if (growth < 0) {
            return `<span style="color:${downColor};font-weight:800">${Numbers.percent(
              growth,
              2
            )} ↓</span>`;
          } else {
            return ``;
          }
        };

        return `
          <div>
            <div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;min-width:100px">
              <div style="color:#fff;opacity:70%;line-height:16px;font-weight:600;margin-bottom:2px;">
                ${p.axisValueLabel}
                ${
                  selectedFilter === "month"
                    ? ` de ${selectedMonth.name}`
                    : ` de ${selectedYear.name}`
                }
              </div>
            </div>

            <div style="line-height:20px">
              <span style="color:#fff">${
                p.data?.value === null
                  ? "---"
                  : Numbers.formatFloat(p.data?.value)
              }</span>
              ${trendHtmlString(p.data?.growth || 0)}
            </div>
          </div>
        `;
      },
    },
    grid: {
      left: 20,
      right: 30,
      top: 30,
      bottom: 20,
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: selectedFilter === "year" ? months : Array.from(daysInMonth),
      axisLine: {
        show: true,
        lineStyle: {
          color: "rgba(217, 217, 217, 0.5)",
        },
      },
      splitLine: {
        lineStyle: {
          color: "rgba(51, 51, 51, 0.55)",
          width: 1,
          type: "solid",
        },
      },
    },
    yAxis: {
      type: "value",

      axisLine: {
        show: true,
        lineStyle: {
          color: "rgba(217, 217, 217, 0.5)",
        },
      },
      splitLine: {
        lineStyle: {
          color: "rgba(51, 51, 51, 0.55)",
          width: 0,
          type: "solid",
        },
      },
    },
    series: [
      {
        name: "Taxa",
        type: "line",
        symbol: "circle",
        symbolSize: 8,
        itemStyle: {
          color: "#6e6eff",
          borderColor: "#6e6eff",
          borderWidth: 2,
        },
        lineStyle: {
          width: 2,
          color: "#6e6eff",
        },
        data: formattedData,
        connectNulls: false,
      },
    ],
  };

  const echartsRef = useRef(null);

  useEffect(() => {
    const chart = echartsRef.current?.getEchartsInstance();
    if (chart) {
      setTimeout(() => chart.resize(), 0);
    }
  }, []);

  const fetchData = async () => {
    if (!onFetchData) {
      return;
    }

    setData([]);

    const payload = { year: selectedYear?.id };
    payload.month = selectedFilter === "month" ? selectedMonth?.id : null;

    const currentMonthIndex = new Date().getMonth();

    if (onFetchData) {
      const results = await onFetchData(payload);

      if (selectedFilter === "year" && skipCurrentMonth) {
        if (typeof results[currentMonthIndex] === "object")
          results[currentMonthIndex] = { value: 0, growth: 0 };
        else results[currentMonthIndex] = 0;
      }

      setData(results);
    } else {
      setData([]);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedFilter, selectedYear, selectedMonth]);

  return (
    <div className={styles.monthYearChartContainer}>
      <div className={styles.monthYearHeader}>
        <p className={styles.title}>{selectedData?.name}</p>
        <div className="d-flex align-center">
          <div className={styles.close} onClick={onClose}>
            <i className="fas fa-times" />
          </div>
        </div>
      </div>

      <div className={styles.monthYearContent}>
        <If cond={loading}>
          <div className={styles.loading}>
            <Loading />
          </div>
        </If>

        <If cond={!loading}>
          <div className={styles.monthYearGrowth}>
            <Trend
              growth={growth}
              selectedFilter={selectedFilter}
              invertColorSign={invertColorSign}
            />

            <div className="d-flex align-center">
              <div className="d-flex align-center gap-10">
                <If cond={enableMonth}>
                  <div
                    className={css({
                      selectMini: true,
                      selected: selectedFilter === "year",
                    })}
                    onClick={() => setSelectedFilter("year")}
                  >
                    Ano
                  </div>
                  <div
                    className={css({
                      selectMini: true,
                      selected: selectedFilter === "month",
                    })}
                    onClick={() => setSelectedFilter("month")}
                  >
                    Mês
                  </div>
                </If>
              </div>
              <If cond={enableMonth}>
                <div className={styles.divider} />
              </If>
              <div className="d-flex align-center">
                <If cond={selectedFilter === "year"}>
                  <ComboFilters
                    options={getLastSixYears(startOn2025)}
                    onSelect={(option) => setSelectedYear(option)}
                    selectedOption={selectedYear}
                  />
                </If>
                <If cond={selectedFilter === "month"}>
                  <ComboFilters
                    options={getAllMonthsUntilNow(
                      true,
                      startOnMonth,
                      startOnYear
                    )}
                    onSelect={(option) => setSelectedMonth(option)}
                    selectedOption={selectedMonth}
                  />
                </If>
              </div>
            </div>
          </div>
          <div className={styles.monthYearChart}>
            <ReactECharts
              ref={echartsRef}
              option={option}
              style={{ width: "100%", height: "100%" }}
              notMerge={true}
              lazyUpdate={true}
              opts={{ renderer: "canvas" }}
            />
          </div>
        </If>
      </div>
    </div>
  );
};
